import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MainContent.css';
import { getTopics } from '../services/topicRegistry';

const bubbles = [
  // Center
  { label: 'What is Web3?', path: '/what-is-web3', size: 220, color: 'primary', x: 0, y: 0, strong: true },
  // Ring
  { label: 'Blockchain', path: '/blockchain', size: 110, color: 'blue', x: 0, y: -170 },
  { label: 'Technology', path: '/technology', size: 100, color: 'blue-light', x: 160, y: -30 },
  { label: 'Development', path: '/development', size: 70, color: 'blue-light', x: 160, y: 60 },
  { label: 'Cryptocurrency', path: '/cryptocurrency', size: 105, color: 'purple', x: 105, y: 70 },
  { label: 'Hackathon', path: '/hackathon', size: 75, color: 'blue-light', x: 0, y: 170 },
  { label: 'Community', path: '/community', size: 125, color: 'blue-light', x: -125, y: 80 },
  { label: 'Career', path: '/career', size: 90, color: 'blue', x: -170, y: -30 },
  { label: 'Industry', path: '/industry', size: 105, color: 'purple', x: -105, y: 10 },
  // Small tags (non-clickable)
  { label: 'For Investor', size: 50, color: 'orange', x: 105, y: -95 },
  { label: 'References', size: 46, color: 'orange', x: -55, y: -60 },
  { label: 'Resources', size: 50, color: 'orange', x: -30, y: 35 },
  { label: 'For Developer', size: 52, color: 'pink', x: -190, y: 50 },
];

const GAP_SCALE = 1.3;
const MIN_GAP = 28; // pixels between bubbles

const Bubble = ({ label, path, diameter, color, x, y, strong, onClick }) => {
  const clickable = Boolean(path);
  // Ensure bubble is at least large enough to fit text width (rough estimate in pixels)
  const estimatedTextWidth = Math.min(320, Math.max(0, label.length * 9 + 28));
  const finalDiameter = Math.max(diameter, estimatedTextWidth);
  return (
    <button
      type="button"
      className={`bubble ${color} ${clickable ? 'clickable' : 'disabled'}`}
      style={{ width: finalDiameter, height: finalDiameter, transform: `translate(-50%, -50%)`, left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)` }}
      onClick={() => clickable && onClick(path)}
      aria-label={label}
    >
      <span className={`bubble-label ${strong ? 'strong' : ''}`}>{label}</span>
    </button>
  );
};

const MainContent = () => {
  const navigate = useNavigate();
  const handleClick = (path) => navigate(path);
  const containerRef = useRef(null);

  // Precompute diameters
  const base = useMemo(() => {
    return bubbles.map((b) => ({
      ...b,
      diameter: b.size,
      x: b.x * GAP_SCALE,
      y: b.y * GAP_SCALE,
      fixed: b.strong || b.label === 'What is Web3?',
    }));
  }, []);

  const [nodes, setNodes] = useState(base);

  // Merge custom topics as outer-ring bubbles; update when registry changes
  useEffect(() => {
    const refresh = () => {
      const customs = getTopics();
      const extras = (customs || []).map((t, i) => ({
        label: t.title,
        path: `/topic/${t.slug}`,
        diameter: 100,
        color: 'blue-light',
        x: (300 + (i % 8) * 80),
        y: (240 + Math.floor(i / 8) * 80),
        fixed: false,
      }));
      setNodes([...base, ...extras]);
    };
    refresh();
    const onUpdate = () => refresh();
    window.addEventListener('topics-updated', onUpdate);
    return () => window.removeEventListener('topics-updated', onUpdate);
  }, [base]);

  // Simple repulsion-based relaxation to avoid overlaps
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const W = el.clientWidth;
    const H = el.clientHeight;
    const maxIters = 1000;
    const nodesCopy = nodes.map((n) => ({ ...n }));

    const clamp = (val, min, max) => Math.min(max, Math.max(min, val));

    for (let iter = 0; iter < maxIters; iter += 1) {
      let moved = false;
      for (let i = 0; i < nodesCopy.length; i += 1) {
        for (let j = i + 1; j < nodesCopy.length; j += 1) {
          const a = nodesCopy[i];
          const b = nodesCopy[j];
          const ra = a.diameter / 2;
          const rb = b.diameter / 2;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.max(0.0001, Math.hypot(dx, dy));
          const required = ra + rb + MIN_GAP - dist;
          if (required > 0) {
            moved = true;
            const ux = dx / dist;
            const uy = dy / dist;
            const push = required / 2 + 0.5; // slight extra to accelerate convergence
            if (!a.fixed) {
              a.x -= ux * push;
              a.y -= uy * push;
            }
            if (!b.fixed) {
              b.x += ux * push;
              b.y += uy * push;
            }
          }
        }
        // Keep within bounds
        const n = nodesCopy[i];
        const r = n.diameter / 2;
        const minX = -W / 2 + r + 8;
        const maxX = W / 2 - r - 8;
        const minY = -H / 2 + r + 8;
        const maxY = H / 2 - r - 8;
        if (!n.fixed) {
          n.x = clamp(n.x, minX, maxX);
          n.y = clamp(n.y, minY, maxY);
        }
      }
      if (!moved) break;
    }
    setNodes(nodesCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="main-content">
      <div className="main-container">
        <button
          type="button"
          className="add-topic-btn"
          onClick={() => navigate('/new-topic')}
          aria-label="Add topic"
          title="Add topic"
        >
          + Add topic
        </button>
        <div className="bubble-map" ref={containerRef}>
          {nodes.map((b, idx) => (
            <Bubble key={`${b.label}-${idx}`} {...b} onClick={handleClick} />
          ))}
        </div>
      </div>
    </main>
  );
};

export default MainContent;